// =============================================================================
// domain/tradingHeatmap.ts — when in the week the money is actually made.
// -----------------------------------------------------------------------------
// A day-of-week × hour-of-day grid. Three readings of the same grid:
//
//   pnl       what that hour of that day is worth
//   trades    how much you trade then — often the more useful one
//   winRate   how often it works then
//
// ── ⚠ THE DAY COMES FROM THE EFFECTIVE DATE, THE HOUR FROM THE CLOCK ──
//
// A trade opened at 23:00 on Monday belongs to Tuesday's SESSION but happened in
// Monday's 11pm hour. The old client gets this right and says so in a comment:
// the row is the effective date's weekday, the column is the raw hour. Deriving
// the weekday from the calendar date instead would put a whole evening's trading
// in the wrong row — and on a book with 100+ evening trades that is a visible
// block in the wrong place.
//
// ── ⚠ A TRADE THAT CANNOT BE PLACED IS COUNTED, NOT DROPPED ──
//
// Imported trades often have no time-in. The old client skips them in silence,
// so a grid built from 460 of 500 trades looks exactly like one built from all
// of them. The count comes back so the screen can say so.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

export interface HeatTrade {
  /** The EFFECTIVE date — resolved by the caller. Decides the ROW. */
  date: string | null;
  /** The clock time the trade was opened. Decides the COLUMN. */
  timeIn: string | null;
  netPnl: number | null;
}

export type HeatMetric = 'pnl' | 'trades' | 'winRate';

export interface HeatCell {
  dow: number;
  hour: number;
  count: number;
  wins: number;
  losses: number;
  pnl: number;
}

export interface HeatGrid {
  /** Rows, in week order. ⚠ Mon–Fri always; a weekend only if it was traded. */
  dows: number[];
  /** Columns. ⚠ At least 6am–4pm, widened to fit whatever was traded. */
  hours: number[];
  cells: Record<string, HeatCell>;
  /** Placed on the grid. */
  placed: number;
  /** ⚠ Had no readable date or hour. Stated, never silently missing. */
  unplaced: number;
}

export const cellKey = (dow: number, hour: number): string => `${dow}_${hour}`;

/** The window the grid always shows, whether or not it was traded. */
const ALWAYS_FROM = 6;
const ALWAYS_TO = 16;

/**
 * Bucket every trade into its weekday and its opening hour.
 *
 * ⚠ MON–FRI ARE ALWAYS ROWS, even at zero. A futures trader expects five
 * weekday rows; a grid that silently omits Wednesday because nothing was traded
 * reads as a broken chart rather than as a quiet week.
 */
export function buildHeatmap(trades: readonly HeatTrade[]): HeatGrid {
  const cells: Record<string, HeatCell> = {};
  const usedDows = new Set<number>();
  let minHour = 24; let maxHour = 0;
  let placed = 0; let unplaced = 0;

  for (const t of trades) {
    const hour = hourOf(t.timeIn);
    const dow = dowOf(t.date);
    if (hour == null || dow == null) { unplaced += 1; continue; }

    const k = cellKey(dow, hour);
    const c = cells[k] ?? { dow, hour, count: 0, wins: 0, losses: 0, pnl: 0 };
    const p = Number(t.netPnl) || 0;
    c.count += 1;
    c.pnl += p;
    if (p > 0) c.wins += 1;
    else if (p < 0) c.losses += 1;
    cells[k] = c;

    usedDows.add(dow);
    if (hour < minHour) minHour = hour;
    if (hour > maxHour) maxHour = hour;
    placed += 1;
  }

  const dows: number[] = [];
  if (usedDows.has(0)) dows.push(0);
  dows.push(1, 2, 3, 4, 5);
  if (usedDows.has(6)) dows.push(6);

  const hours: number[] = [];
  if (placed) {
    const from = Math.min(minHour, ALWAYS_FROM);
    const to = Math.max(maxHour, ALWAYS_TO);
    for (let h = from; h <= to; h += 1) hours.push(h);
  }

  return { dows, hours, cells, placed, unplaced };
}

/** Hour 0–23 from a `HH:MM` clock string, or null. */
export function hourOf(clock: string | null): number | null {
  if (!clock) return null;
  const h = Number(String(clock).split(':')[0]);
  return Number.isInteger(h) && h >= 0 && h <= 23 ? h : null;
}

/** Weekday 0–6 from an ISO date. ⚠ Local midnight, as everywhere in this product. */
export function dowOf(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  const n = d.getDay();
  return Number.isInteger(n) ? n : null;
}

/**
 * The number a cell is coloured by, under the active metric.
 *
 * ⚠ WIN RATE IS CENTRED ON 50%, not on zero — a cell that wins half the time is
 * neutral, not "half as good as the best cell". Measured any other way, an
 * average hour would glow as brightly as a great one.
 */
export function heatValue(cell: HeatCell, metric: HeatMetric): number {
  if (metric === 'trades') return cell.count;
  if (metric === 'pnl') return cell.pnl;
  const decided = cell.wins + cell.losses;
  return decided ? ((100 * cell.wins) / decided) - 50 : 0;
}

/** The largest magnitude on the grid — the scale every cell is shaded against. */
export function heatMax(grid: HeatGrid, metric: HeatMetric): number {
  let max = 0;
  for (const c of Object.values(grid.cells)) {
    const v = Math.abs(heatValue(c, metric));
    if (v > max) max = v;
  }
  return max;
}

/**
 * 0–1, how strongly a cell is shaded.
 *
 * ⚠ THE FLOOR IS IN THE CALLER'S ALPHA, not here. A cell with a real trade in it
 * has to be visibly different from an empty one even when its value is tiny,
 * which is why the old client shades `0.18 + intensity × 0.55` rather than
 * straight intensity.
 */
export function heatIntensity(cell: HeatCell, metric: HeatMetric, max: number): number {
  if (max <= 0) return 0;
  return Math.min(1, Math.abs(heatValue(cell, metric)) / max);
}

export const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** `12a`, `9a`, `12p`, `4p`. */
export function hourLabel(h: number): string {
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}
